import React, { createContext, useContext, useState, useEffect } from "react";
import { authAPI, getToken, getUser, setUser as persistUser } from "../lib/auth";
import { getEffectivePermissions } from "../lib/permissions";
import { toast } from "sonner";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      const storedToken = getToken();
      const storedUser = getUser();
      
      if (storedToken && storedUser) {
        setToken(storedToken);
        setUser(storedUser);
        setIsAuthenticated(true);
        
        // Verify token is still valid and refresh user (picks up permission changes)
        try {
          const freshUser = await authAPI.me();
          setUser(freshUser);
          persistUser(freshUser);
        } catch (error) {
          // Token is invalid, clear auth silently during initialization
          console.warn("Token validation failed during initialization:", error.message);
          logout(false, 'token_validation_failed'); // Don't show success message for expired token
        }
      }
    } catch (error) {
      console.error("Auth initialization error:", error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials) => {
    try {
      const { token, user } = await authAPI.login(credentials);
      setToken(token);
      setUser(user);
      setIsAuthenticated(true);
      toast.success(`Welcome back, ${user.full_name}!`);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || "Login failed";
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = (showMessage = true, reason = 'manual') => {
    console.log(`🔓 Logout triggered - Reason: ${reason}`);
    authAPI.logout();
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    if (showMessage) {
      toast.success("Logged out successfully");
    }
  };

  const register = async (userData) => {
    try {
      const newUser = await authAPI.register(userData);
      toast.success(`User ${newUser.username} created successfully`);
      return { success: true, user: newUser };
    } catch (error) {
      const message = error.response?.data?.detail || "Registration failed";
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const effectivePermissions = getEffectivePermissions(user);
  const hasModule = (module) =>
    !!user?.is_super_admin || (effectivePermissions[module] && effectivePermissions[module] !== "none");
  const canEditModule = (module) =>
    !!user?.is_super_admin || effectivePermissions[module] === "edit";

  const value = {
    user,
    token,
    loading,
    isAuthenticated,
    login,
    logout,
    register,
    isAdmin: user?.role === "admin",
    isStaff: user?.role === "staff",
    isSuperAdmin: !!user?.is_super_admin,
    permissions: effectivePermissions,
    hasModule,
    canEditModule,
    canEditCustomers: user?.role === "admin" || user?.role === "staff",
    canEditSubscriptions: user?.role === "admin",
    canEditInteractions: user?.role === "admin" || user?.role === "staff",
    canEditAssets: user?.role === "admin" || user?.role === "staff"
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};